import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { HUD } from "./theme";

const inputCls = `h-10 rounded-xl px-3 text-sm border ${HUD.input}`;
const Box = ({ title, desc, children, testid }) => (
  <div className={`${HUD.panel} p-5 space-y-4`} data-testid={testid}>
    <div><h3 className="text-white font-['Outfit'] text-lg">{title}</h3>{desc && <p className="text-xs text-gray-400 mt-1">{desc}</p>}</div>
    {children}
  </div>
);

export const CardFeeSection = ({ f, setF }) => (
  <Box title="Card processing fee" desc="Added as its own line on invoices, pay links and deposits. You can switch it off per invoice or pay link." testid="jarvis-card-fee-section">
    <div className="flex flex-wrap items-center gap-4">
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <Switch checked={f.card_fee_default_on ?? true} onCheckedChange={(v) => setF({ ...f, card_fee_default_on: v })} data-testid="jarvis-card-fee-toggle" /> Charge the fee by default
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="number" step="0.01" min="0" max="10" value={f.card_fee_pct ?? 3.79} onChange={(e) => setF({ ...f, card_fee_pct: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid="jarvis-card-fee-pct" className={`${inputCls} w-24`} /> %
      </label>
    </div>
  </Box>
);

const ListEditor = ({ items, setItems, fields, blank, testid }) => (
  <div className="space-y-3">
    {items.map((it, i) => (
      <div key={it.id || i} className="rounded-xl border border-cyan-400/10 p-3 grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-start relative pr-10" data-testid={`${testid}-${i}`}>
        {fields.map(([k, ph, type]) => (
          <input key={k} type={type || "text"} step="0.05" placeholder={ph} value={it[k] ?? ""} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: type === "number" ? Number(e.target.value) : e.target.value } : x)))}
            data-testid={`${testid}-${k}-${i}`} className={`${inputCls} ${k === "description" ? "sm:col-span-2" : ""}`} />
        ))}
        <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="absolute top-3 right-3 text-gray-500 hover:text-rose-300 h-10" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    ))}
    <button onClick={() => setItems([...items, blank])} className={`${HUD.ghost} h-9 px-3 text-xs flex items-center gap-1`} data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5" /> Add</button>
  </div>
);

export const BookingOptionsSection = ({ f, setF }) => (
  <>
    <Box title="Vehicle conditions (Book Now)" desc="Customers must pick one before getting a quote. Price × multiplier (1 = no change, 1.3 = +30%). Add a photo URL to show an example." testid="jarvis-conditions-section">
      <ListEditor items={f.vehicle_conditions || []} setItems={(v) => setF({ ...f, vehicle_conditions: v })} testid="jarvis-cond"
        fields={[["label", "Name"], ["multiplier", "Price multiplier", "number"], ["description", "Description"], ["image_url", "Photo URL (optional)"]]}
        blank={{ label: "", multiplier: 1, description: "", image_url: "" }} />
    </Box>
    <Box title="Service categories (Book Now tabs)" desc="Tabs shown above the services. Assign each service to a tab in the Services admin." testid="jarvis-categories-section">
      <ListEditor items={f.service_categories || []} setItems={(v) => setF({ ...f, service_categories: v })} testid="jarvis-cat"
        fields={[["label", "Tab name"]]} blank={{ label: "" }} />
    </Box>
  </>
);

const ALERT_KINDS = [["booking", "New booking"], ["quote", "New quote request"], ["payment", "Payment received"], ["invoice_viewed", "Invoice opened"], ["link_viewed", "Pay link opened"]];

export const AlertsSection = ({ f, setF }) => (
  <Box title="Owner alerts" desc="Every alert shows in the bell at the top of the dashboard. Switched-on alerts are also texted to your top 2 team numbers and emailed to your business email." testid="jarvis-alerts-section">
    <div className="grid sm:grid-cols-2 gap-2">
      {ALERT_KINDS.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between gap-3 rounded-xl border border-cyan-400/10 p-3 text-sm text-gray-300">
          {l}
          <Switch checked={(f.alert_kinds || {})[k] !== false} onCheckedChange={(v) => setF({ ...f, alert_kinds: { ...(f.alert_kinds || {}), [k]: v } })} data-testid={`jarvis-alert-${k}`} />
        </label>
      ))}
    </div>
  </Box>
);

export const SETTINGS_TABS = [["general", "General"], ["homepage", "Homepage"], ["payments", "Payments & fees"], ["booking", "Services"], ["calendar", "Calendar"], ["phone", "Phone & AI"], ["business", "Business & invoices"]];

export const SettingsTabs = ({ tab, setTab }) => (
  <div className="flex flex-wrap gap-1 p-1 rounded-2xl border border-cyan-400/10 bg-black/40 sticky top-2 z-10 backdrop-blur" data-testid="jarvis-settings-tabs">
    {SETTINGS_TABS.map(([k, l]) => (
      <button key={k} onClick={() => setTab(k)} data-testid={`jarvis-settings-tab-${k}`}
        className={`h-9 px-4 rounded-xl text-xs transition-colors ${tab === k ? "bg-cyan-400/15 text-white border border-cyan-400/40" : "text-gray-400 hover:text-white"}`}>{l}</button>
    ))}
  </div>
);
